import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
  useVisaApplicationDelete,
  useVisaApplicationGetFilters,
  useVisaApplicationList,
  useVisaApplicationExport,
} from '../hooks/use-visa-application.hook';
import { useStatusList } from '../../statuses/hooks/use-status.hook';
import type { ColumnConfig } from '../../../../common/PaginatedTable';
import type {
  VisaApplication,
  VisaApplicationExportRequest,
} from '../types/visa-application.types';
import type { Status } from '../../statuses/types/status.types';
import VisaApplicationTable from '../components/visa-application-table.component';
import { DEFAULT_PAGINATED_TABLE_DATA } from '../../../../../const/default-paginated-table-data.const';
import ActionButtons from '../../../../common/ActionButtons';
import { useFilters } from '../../../../../hooks/use-filters.hook';
import { message, Button, Modal, Form, Input, Space, Checkbox, Alert } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../../../../../states/user.atom';
import { rbac } from 'permissions/permissions';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import { extractCreateError } from '@services/extract-api-error.service';
import VisaApplicationBulkImportContainer from './visa-application-bulk-import.container';
import ExportModal from '../../../../common/ExportModal';
import { useNavigate } from 'react-router-dom';
import { SelectedFilter } from '../../../../../types/filters.types';
import { useSearchParams } from 'react-router-dom';
import { useJobCreate, useJobList, useJobGet } from '../../jobs/hooks/use-job.hook';
import { JobStatusEnum } from '../../jobs/types/job.types';
import { useWorkflowList } from '../../workflow/hooks/use-workflow.hook';
import SelectField from '../../../../common/SelectField';
import type { UseListParams } from '../../../../../types/pagination.types';
import { useLocalStorage } from '../../../../../hooks/use-local-storage.hook';
import { VISA_APPLICATION_COLUMN_VISIBILITY_KEY } from '../../../../../const/local-storage-keys.const';
import ColumnVisibilitySelector, {
  ColumnOption,
} from '../../../../common/ColumnVisibilitySelector';
import { formatDate } from '../../../../../utils/date.util';

/**
 * Parse filters from URL query string to SelectedFilter array
 * Format: filters[key][op]=in&filters[key][val][0]=value1&filters[key][val][1]=value2
 */
const parseFiltersFromUrl = (filtersParam: string | null): SelectedFilter[] => {
  if (!filtersParam) return [];

  const filters: Record<string, string[]> = {};

  // Parse the filter string
  const filterPairs = filtersParam.split('&');
  filterPairs.forEach((pair) => {
    const [key, value] = pair.split('=');
    if (key && value) {
      // Match patterns like filters[visaStatus][val][0]
      const match = key.match(/filters\[([^\]]+)\]\[val\]\[(\d+)\]/);
      if (match) {
        const filterKey = match[1];
        if (!filters[filterKey]) {
          filters[filterKey] = [];
        }
        filters[filterKey].push(decodeURIComponent(value));
      }
    }
  });

  // Convert to SelectedFilter format
  return Object.entries(filters).map(([key, values]) => ({
    key,
    values,
    operation: 'in' as const,
  }));
};

/**
 * Extract visaStatus value from filters
 */
const getVisaStatusFromFilters = (filters: SelectedFilter[]): string | null => {
  const visaStatusFilter = filters.find((f) => f.key === 'visaStatus');
  if (visaStatusFilter && visaStatusFilter.values && visaStatusFilter.values.length > 0) {
    return visaStatusFilter.values[0];
  }
  return null;
};

/**
 * Remove visaStatus filter from filters array
 */
const removeVisaStatusFilter = (filters: SelectedFilter[]): SelectedFilter[] => {
  return filters.filter((f) => f.key !== 'visaStatus');
};

const ALL_APPLICATIONS_TAB_KEY = 'all';

const VisaApplicationTableContainer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getUserPermissions } = useAuthUser();
  const userPermissions = getUserPermissions();
  const [searchParams] = useSearchParams();

  const {
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
  } = useFilters();

  // Fetch statuses for tabs
  const { data: statusesData } = useStatusList({ page: 1, limit: 100 });
  const statuses = statusesData?.docs || [];

  // Use 'all' as default tab to show all applications
  const [selectedTab, setSelectedTab] = useState<string | null>(ALL_APPLICATIONS_TAB_KEY);
  const isInitializedRef = useRef(false);

  // Don't apply default status filter on mount - show all applications by default
  useEffect(() => {
    if (isInitializedRef.current) return;

    // Remove any existing visaStatus filter to show all applications
    const filtersParam = searchParams.get('filters');
    const currentFilters = parseFiltersFromUrl(filtersParam);
    const filtersWithoutVisaStatus = removeVisaStatusFilter(currentFilters);

    if (filtersWithoutVisaStatus.length !== currentFilters.length) {
      // Only update if there was a visaStatus filter to remove
      handleFilterChange(filtersWithoutVisaStatus);
    }

    setSelectedTab(ALL_APPLICATIONS_TAB_KEY);
    isInitializedRef.current = true;
  }, [handleFilterChange, searchParams]);

  const { data: availableFilters, isLoading: isAvailableFiltersLoading } =
    useVisaApplicationGetFilters();

  // Transform visaStatus filter values from IDs to objects with statusCode labels
  const transformedFilters = useMemo(() => {
    if (!availableFilters?.data || !statuses.length) {
      return availableFilters?.data;
    }

    // Create a map of status ID to statusCode for quick lookup
    const statusMap = new Map(statuses.map((status) => [status.id, status.statusCode]));

    return availableFilters.data.map((filter) => {
      if (filter.filterKey === 'visaStatus') {
        // Transform IDs to objects with label (statusCode) and value (id)
        return {
          ...filter,
          filterValues: filter.filterValues.map((id: string) => ({
            label: statusMap.get(id) || id,
            value: id,
          })),
        };
      }
      return filter;
    });
  }, [availableFilters?.data, statuses]);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [createJobModalVisible, setCreateJobModalVisible] = useState(false);
  const [createJobForm] = Form.useForm();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined);
  const [duplicateTemplateWarning, setDuplicateTemplateWarning] = useState(false);
  const { data, isLoading, isFetching } = useVisaApplicationList(queryParams);
  const { docs: tableData = [] } = data ?? DEFAULT_PAGINATED_TABLE_DATA;

  const deleteMutation = useVisaApplicationDelete();
  const createJobMutation = useJobCreate();

  // Hook to fetch job templates (jobs with isTemplate: true)
  const useJobTemplateList = (params: UseListParams) => {
    const filterString = 'filters[isTemplate][op]=eq&filters[isTemplate][val][0]=true';
    return useJobList({ ...params, filters: filterString });
  };

  // Fetch selected template details
  const { data: selectedTemplateData } = useJobGet(selectedTemplateId || '', {
    enabled: !!selectedTemplateId,
  });

  const {
    data: exportData,
    isPending: isExportLoading,
    mutateAsync: exportVisaApplications,
  } = useVisaApplicationExport({
    enabled: false,
  });

  const handleExportVisaApplications = useCallback(
    async (email?: string) => {
      try {
        if (!selectedTab || selectedTab === ALL_APPLICATIONS_TAB_KEY) {
          message.error(t('visaApplications.export.noStatusSelected'));
          return;
        }
        const payload: VisaApplicationExportRequest = {
          email,
          visaStatus: selectedTab,
        };
        const result = await exportVisaApplications(payload);
        message.success(t('visaApplications.export.success'));
        if (result?.data?.jobId) {
          navigate(`/dashboard/jobs/${result.data.jobId}`);
        }
      } catch (error) {
        console.error('Export error:', error);
        message.error(t('visaApplications.export.error'));
      }
    },
    [exportVisaApplications, t, navigate, selectedTab]
  );

  const handleDelete = useCallback(
    async (record: VisaApplication) => {
      try {
        await deleteMutation.mutateAsync((record as any).id as string);
        message.success(t('crud.delete.success', { entity: t('visaApplications.title') }));
      } catch (error) {
        const errorMessage = extractCreateError(
          error,
          t('crud.delete.error', { entity: t('visaApplications.title') })
        );
        message.error(errorMessage);
      }
    },
    [t, deleteMutation]
  );

  // Handle filter changes - allow visaStatus as a regular filter since tabs are commented out
  const handleVisaFilterChange = useCallback(
    (filters: SelectedFilter[]) => {
      // Since tabs are commented out, just pass through filters including visaStatus
      handleFilterChange(filters);
    },
    [handleFilterChange]
  );

  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      if (sorter && sorter.field) {
        let sortOrder: 'asc' | 'desc';
        if (sorter.order === 'ascend') {
          sortOrder = 'asc';
        } else if (sorter.order === 'descend') {
          sortOrder = 'desc';
        } else {
          sortOrder = 'asc';
        }
        handleSortChange(sorter.field as string, sortOrder);
      } else {
        handleSortChange('createdAt', 'desc');
      }
    },
    [handleSortChange]
  );

  const handleTabChange = useCallback(
    (tabKey: string) => {
      setSelectedTab(tabKey);

      // Get current filters (excluding visaStatus)
      const filtersParam = searchParams.get('filters');
      const currentFilters = parseFiltersFromUrl(filtersParam);
      const filtersWithoutVisaStatus = removeVisaStatusFilter(currentFilters);

      // If "all" is selected, don't add visaStatus filter
      if (tabKey === ALL_APPLICATIONS_TAB_KEY) {
        handleFilterChange(filtersWithoutVisaStatus);
        return;
      }

      // Otherwise, add new visaStatus filter for the selected status
      const updatedFilters: SelectedFilter[] = [
        ...filtersWithoutVisaStatus,
        {
          key: 'visaStatus',
          values: [tabKey],
          operation: 'in',
        },
      ];

      handleFilterChange(updatedFilters);
    },
    [handleFilterChange, searchParams]
  );

  // Column visibility state with localStorage persistence
  // Include visaStatus column by default when "All Applications" tab is active
  const defaultVisibleColumns = useMemo(
    () => ['passportNumber', 'applicationNumber', 'visaType', 'visaStatus', 'actions'],
    []
  );
  const [visibleColumnKeys, setVisibleColumnKeys] = useLocalStorage<string[]>(
    VISA_APPLICATION_COLUMN_VISIBILITY_KEY,
    defaultVisibleColumns
  );

  // Define all possible columns
  const allColumnDefinitions = useMemo<ColumnConfig<VisaApplication>[]>(
    () => [
      {
        title: t('visaApplications.passportNumber.label'),
        key: 'passportNumber',
        dataIndex: 'passportNumber',
        sorter: true,
      },
      {
        title: t('visaApplications.applicationNumber.label'),
        key: 'applicationNumber',
        dataIndex: 'applicationNumber',
        sorter: true,
      },
      {
        title: t('visaApplications.visaType.label'),
        key: 'visaType',
        dataIndex: 'visaType',
        sorter: true,
      },
      {
        title: t('visaApplications.visaStatus.label'),
        key: 'visaStatus',
        dataIndex: 'visaStatus',
        render: (_: any, record: VisaApplication) => {
          if (typeof record.visaStatus === 'object' && record.visaStatus !== null) {
            return record.visaStatus.statusCode || '-';
          }
          return typeof record.visaStatus === 'string' ? record.visaStatus : '-';
        },
        sorter: true,
      },
      {
        title: t('visaApplications.visaName.label'),
        key: 'visaName',
        dataIndex: 'visaName',
        sorter: true,
      },
      {
        title: t('visaApplications.visaSponsor.label'),
        key: 'visaSponsor',
        dataIndex: 'visaSponsor',
      },
      {
        title: t('visaApplications.visaUidNumber.label'),
        key: 'visaUidNumber',
        dataIndex: 'visaUidNumber',
      },
      {
        title: t('visaApplications.visaFileNumber.label'),
        key: 'visaFileNumber',
        dataIndex: 'visaFileNumber',
      },
      {
        title: t('visaApplications.visaIssueDate.label'),
        key: 'visaIssueDate',
        dataIndex: 'visaIssueDate',
        render: (date: Date | string | null | undefined) => {
          if (!date) return '-';
          const dateStr = typeof date === 'string' ? date : date.toString();
          return formatDate(dateStr);
        },
        sorter: true,
      },
      {
        title: t('visaApplications.visaExpiryDate.label'),
        key: 'visaExpiryDate',
        dataIndex: 'visaExpiryDate',
        render: (date: Date | string | null | undefined) => {
          if (!date) return '-';
          const dateStr = typeof date === 'string' ? date : date.toString();
          return formatDate(dateStr);
        },
        sorter: true,
      },
      {
        title: 'MRZ',
        key: 'mrz',
        dataIndex: 'mrz',
      },
      // Client Data
      {
        title: 'Client Name',
        key: 'clientData.clientName',
        dataIndex: ['clientData', 'clientName'],
      },
      {
        title: 'Org Level 1',
        key: 'clientData.orgLevel1',
        dataIndex: ['clientData', 'orgLevel1'],
      },
      {
        title: 'Org Level 2',
        key: 'clientData.orgLevel2',
        dataIndex: ['clientData', 'orgLevel2'],
      },
      {
        title: 'Org Level 3',
        key: 'clientData.orgLevel3',
        dataIndex: ['clientData', 'orgLevel3'],
      },
      {
        title: 'Trade License Number',
        key: 'clientData.tradeLicenseNumber',
        dataIndex: ['clientData', 'tradeLicenseNumber'],
      },
      {
        title: 'Company Address',
        key: 'clientData.companyAddress',
        dataIndex: ['clientData', 'companyAddress'],
      },
      // Application Data
      {
        title: 'Service Name',
        key: 'applicationData.serviceName',
        dataIndex: ['applicationData', 'serviceName'],
      },
      {
        title: 'Service Stage',
        key: 'applicationData.serviceStage',
        dataIndex: ['applicationData', 'serviceStage'],
      },
      {
        title: 'Service Outcome',
        key: 'applicationData.serviceOutcome',
        dataIndex: ['applicationData', 'serviceOutcome'],
      },
      {
        title: 'Document Number',
        key: 'applicationData.documentNumber',
        dataIndex: ['applicationData', 'documentNumber'],
      },
      {
        title: 'Machine ID',
        key: 'applicationData.machineId',
        dataIndex: ['applicationData', 'machineId'],
      },
      {
        title: 'Incident ID',
        key: 'applicationData.incidentId',
        dataIndex: ['applicationData', 'incidentId'],
      },
      // Employee Data
      {
        title: 'Candidate ID',
        key: 'employeeData.candidateId',
        dataIndex: ['employeeData', 'candidateId'],
      },
      {
        title: 'Employee ID',
        key: 'employeeData.employeeId',
        dataIndex: ['employeeData', 'employeeId'],
      },
      {
        title: 'Probot ID',
        key: 'employeeData.probotId',
        dataIndex: ['employeeData', 'probotId'],
      },
      {
        title: 'Company Designation',
        key: 'employeeData.companyDesignation',
        dataIndex: ['employeeData', 'companyDesignation'],
      },
      {
        title: 'Mapped Designation',
        key: 'employeeData.mappedDesignation',
        dataIndex: ['employeeData', 'mappedDesignation'],
      },
      {
        title: 'Work Type',
        key: 'employeeData.workType',
        dataIndex: ['employeeData', 'workType'],
      },
      {
        title: 'Home Address',
        key: 'employeeData.addressHome',
        dataIndex: ['employeeData', 'addressHome'],
      },
      {
        title: 'Local Address',
        key: 'employeeData.addressLocal',
        dataIndex: ['employeeData', 'addressLocal'],
      },
      {
        title: 'Home Phone',
        key: 'employeeData.phoneHome',
        dataIndex: ['employeeData', 'phoneHome'],
      },
      {
        title: 'Education Level',
        key: 'employeeData.educationLevel',
        dataIndex: ['employeeData', 'educationLevel'],
      },
      {
        title: 'Marital Status',
        key: 'employeeData.maritalStatus',
        dataIndex: ['employeeData', 'maritalStatus'],
      },
      {
        title: 'First Language',
        key: 'employeeData.firstLanguage',
        dataIndex: ['employeeData', 'firstLanguage'],
      },
      {
        title: 'Number of Children',
        key: 'employeeData.numberOfChildren',
        dataIndex: ['employeeData', 'numberOfChildren'],
      },
      {
        title: 'Typed First Name',
        key: 'employeeData.typedFirstName',
        dataIndex: ['employeeData', 'typedFirstName'],
      },
      {
        title: 'Typed Last Name',
        key: 'employeeData.typedLastName',
        dataIndex: ['employeeData', 'typedLastName'],
      },
      {
        title: 'Date of Birth',
        key: 'employeeData.typedDateOfBirth',
        dataIndex: ['employeeData', 'typedDateOfBirth'],
        render: (date: Date | string | null | undefined) => {
          if (!date) return '-';
          const dateStr = typeof date === 'string' ? date : date.toString();
          return formatDate(dateStr);
        },
      },
      {
        title: 'Gender',
        key: 'employeeData.typedGender',
        dataIndex: ['employeeData', 'typedGender'],
      },
      {
        title: 'Nationality',
        key: 'employeeData.typedNationality',
        dataIndex: ['employeeData', 'typedNationality'],
      },
      // Passport Data
      {
        title: 'Passport First Name',
        key: 'passportData.passportFirstName',
        dataIndex: ['passportData', 'passportFirstName'],
      },
      {
        title: 'Passport Last Name',
        key: 'passportData.passportLastName',
        dataIndex: ['passportData', 'passportLastName'],
      },
      {
        title: 'Passport Issue Date',
        key: 'passportData.passportIssueDate',
        dataIndex: ['passportData', 'passportIssueDate'],
        render: (date: Date | string | null | undefined) => {
          if (!date) return '-';
          const dateStr = typeof date === 'string' ? date : date.toString();
          return formatDate(dateStr);
        },
      },
      {
        title: 'Passport Expiry Date',
        key: 'passportData.passportExpiryDate',
        dataIndex: ['passportData', 'passportExpiryDate'],
        render: (date: Date | string | null | undefined) => {
          if (!date) return '-';
          const dateStr = typeof date === 'string' ? date : date.toString();
          return formatDate(dateStr);
        },
      },
      {
        title: 'Passport Date of Birth',
        key: 'passportData.passportDateOfBirth',
        dataIndex: ['passportData', 'passportDateOfBirth'],
        render: (date: Date | string | null | undefined) => {
          if (!date) return '-';
          const dateStr = typeof date === 'string' ? date : date.toString();
          return formatDate(dateStr);
        },
      },
      {
        title: 'Passport Gender',
        key: 'passportData.passportGender',
        dataIndex: ['passportData', 'passportGender'],
      },
      {
        title: 'Passport Nationality',
        key: 'passportData.passportNationality',
        dataIndex: ['passportData', 'passportNationality'],
      },
      {
        title: 'Passport Issuing Authority',
        key: 'passportData.passportIssuingAuthority',
        dataIndex: ['passportData', 'passportIssuingAuthority'],
      },
      {
        title: 'Passport Place of Birth',
        key: 'passportData.passportPlaceOfBirth',
        dataIndex: ['passportData', 'passportPlaceOfBirth'],
      },
      // EID Data
      {
        title: 'EID First Name',
        key: 'eidData.eidFirstName',
        dataIndex: ['eidData', 'eidFirstName'],
      },
      {
        title: 'EID Last Name',
        key: 'eidData.eidLastName',
        dataIndex: ['eidData', 'eidLastName'],
      },
      {
        title: 'EID Number',
        key: 'eidData.eidNumber',
        dataIndex: ['eidData', 'eidNumber'],
      },
      {
        title: 'EID Issue Date',
        key: 'eidData.eidIssueDate',
        dataIndex: ['eidData', 'eidIssueDate'],
        render: (date: Date | string | null | undefined) => {
          if (!date) return '-';
          const dateStr = typeof date === 'string' ? date : date.toString();
          return formatDate(dateStr);
        },
      },
      {
        title: 'EID Expiry Date',
        key: 'eidData.eidExpiryDate',
        dataIndex: ['eidData', 'eidExpiryDate'],
        render: (date: Date | string | null | undefined) => {
          if (!date) return '-';
          const dateStr = typeof date === 'string' ? date : date.toString();
          return formatDate(dateStr);
        },
      },
      {
        title: 'EID Occupation',
        key: 'eidData.eidOccupation',
        dataIndex: ['eidData', 'eidOccupation'],
      },
      {
        title: 'EID Card Number',
        key: 'eidData.eidCardNumber',
        dataIndex: ['eidData', 'eidCardNumber'],
      },
      // Visa Data
      {
        title: 'Visa Emirate',
        key: 'visaData.visaEmirate',
        dataIndex: ['visaData', 'visaEmirate'],
      },
      {
        title: 'Visa Designation',
        key: 'visaData.visaDesignation',
        dataIndex: ['visaData', 'visaDesignation'],
      },
      // Driver's License
      {
        title: 'DL Full Name',
        key: 'driversLicense.dlFullName',
        dataIndex: ['driversLicense', 'dlFullName'],
      },
      {
        title: 'DL Number',
        key: 'driversLicense.dlNumber',
        dataIndex: ['driversLicense', 'dlNumber'],
      },
      {
        title: 'DL Issue Date',
        key: 'driversLicense.dlIssueDate',
        dataIndex: ['driversLicense', 'dlIssueDate'],
        render: (date: Date | string | null | undefined) => {
          if (!date) return '-';
          const dateStr = typeof date === 'string' ? date : date.toString();
          return formatDate(dateStr);
        },
      },
      {
        title: 'DL Expiry Date',
        key: 'driversLicense.dlExpiryDate',
        dataIndex: ['driversLicense', 'dlExpiryDate'],
        render: (date: Date | string | null | undefined) => {
          if (!date) return '-';
          const dateStr = typeof date === 'string' ? date : date.toString();
          return formatDate(dateStr);
        },
      },
      // Contract
      {
        title: 'Basic Salary Amount',
        key: 'contract.basicSalaryAmount',
        dataIndex: ['contract', 'basicSalaryAmount'],
        render: (amount: number) => (amount ? amount.toLocaleString() : '-'),
      },
      {
        title: 'Housing Amount',
        key: 'contract.housingAmount',
        dataIndex: ['contract', 'housingAmount'],
        render: (amount: number) => (amount ? amount.toLocaleString() : '-'),
      },
      {
        title: 'Transport Allowance',
        key: 'contract.transportAllowanceAmount',
        dataIndex: ['contract', 'transportAllowanceAmount'],
        render: (amount: number) => (amount ? amount.toLocaleString() : '-'),
      },
      // Recruiter Info
      {
        title: 'Recruiter Name',
        key: 'recruiterInfo.recruiterName',
        dataIndex: ['recruiterInfo', 'recruiterName'],
      },
      {
        title: 'Recruiter Email',
        key: 'recruiterInfo.recruiterEmail',
        dataIndex: ['recruiterInfo', 'recruiterEmail'],
      },
      {
        title: 'Recruiter Telephone',
        key: 'recruiterInfo.recruiterTelephone',
        dataIndex: ['recruiterInfo', 'recruiterTelephone'],
      },
      // Snapshot Table
      {
        title: 'Application Status',
        key: 'snapshotTable.applicationStatus',
        dataIndex: ['snapshotTable', 'applicationStatus'],
      },
      {
        title: 'Application Type',
        key: 'snapshotTable.applicationType',
        dataIndex: ['snapshotTable', 'applicationType'],
      },
      {
        title: 'Application Cost',
        key: 'snapshotTable.applicationCost',
        dataIndex: ['snapshotTable', 'applicationCost'],
        render: (cost: number) => (cost ? cost.toLocaleString() : '-'),
      },
      // Flight Info
      {
        title: 'Airline Name',
        key: 'flightInfo.airlineName',
        dataIndex: ['flightInfo', 'airlineName'],
      },
      {
        title: 'Flight Number',
        key: 'flightInfo.flightNumber',
        dataIndex: ['flightInfo', 'flightNumber'],
      },
      {
        title: 'Arrival Airport',
        key: 'flightInfo.arrivalAirport',
        dataIndex: ['flightInfo', 'arrivalAirport'],
      },
      {
        title: 'Arrival Date',
        key: 'flightInfo.arrivalDate',
        dataIndex: ['flightInfo', 'arrivalDate'],
        render: (date: Date | string | null | undefined) => {
          if (!date) return '-';
          const dateStr = typeof date === 'string' ? date : date.toString();
          return formatDate(dateStr);
        },
      },
      // Ticket Management
      {
        title: 'Incident Number',
        key: 'ticketManagement.incidentNumber',
        dataIndex: ['ticketManagement', 'incidentNumber'],
      },
      {
        title: 'Ticket Subject',
        key: 'ticketManagement.subject',
        dataIndex: ['ticketManagement', 'subject'],
      },
      {
        title: 'Ticket Status',
        key: 'ticketManagement.status',
        dataIndex: ['ticketManagement', 'status'],
      },
      {
        title: 'Approval Status',
        key: 'ticketManagement.approvalStatus',
        dataIndex: ['ticketManagement', 'approvalStatus'],
      },
      // Actions column (always visible)
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        fixed: 'right' as const,
        render: (_: any, record: any) => (
          <ActionButtons
            showEdit={rbac.hasAccess(userPermissions, 'visaApplication.update')}
            showDelete={rbac.hasAccess(userPermissions, 'visaApplication.delete')}
            showView={rbac.hasAccess(userPermissions, 'visaApplication.view')}
            record={record}
            baseUrl="/dashboard/visa-application"
            onDelete={() => handleDelete(record)}
            isDeleting={deleteMutation.isPending && deleteMutation.variables === record.id}
          />
        ),
      },
    ],
    [t, userPermissions, handleDelete, deleteMutation.isPending, deleteMutation.variables]
  );

  // Column options for the selector
  const columnOptions = useMemo<ColumnOption[]>(() => {
    return allColumnDefinitions
      .filter((col) => col.key !== 'actions') // Exclude actions from selector
      .map((col) => {
        // Determine category based on key
        let category = 'General';
        if (col.key.startsWith('clientData.')) category = 'Client Data';
        else if (col.key.startsWith('applicationData.')) category = 'Application Data';
        else if (col.key.startsWith('employeeData.')) category = 'Employee Data';
        else if (col.key.startsWith('passportData.')) category = 'Passport Data';
        else if (col.key.startsWith('eidData.')) category = 'Emirates ID Data';
        else if (col.key.startsWith('visaData.')) category = 'Visa Data';
        else if (col.key.startsWith('driversLicense.')) category = "Driver's License";
        else if (col.key.startsWith('contract.')) category = 'Contract';
        else if (col.key.startsWith('recruiterInfo.')) category = 'Recruiter Info';
        else if (col.key.startsWith('snapshotTable.')) category = 'Snapshot';
        else if (col.key.startsWith('flightInfo.')) category = 'Flight Info';
        else if (col.key.startsWith('ticketManagement.')) category = 'Ticket Management';

        return {
          key: col.key,
          label: col.title as string,
          category,
        };
      });
  }, [allColumnDefinitions]);

  // Filter columns based on visibility selection
  const columns = useMemo<ColumnConfig<VisaApplication>[]>(() => {
    // Always include actions column
    const actionsColumn = allColumnDefinitions.find((col) => col.key === 'actions');

    // Create a map for quick lookup
    const columnMap = new Map(allColumnDefinitions.map((col) => [col.key, col]));

    // Filter and sort columns based on selection order in visibleColumnKeys
    const visibleColumns = visibleColumnKeys
      .filter((key) => key !== 'actions') // Exclude actions for now
      .map((key) => columnMap.get(key))
      .filter((col): col is ColumnConfig<VisaApplication> => col !== undefined);

    // Ensure actions is always included at the end
    if (actionsColumn) {
      visibleColumns.push(actionsColumn);
    }

    return visibleColumns;
  }, [allColumnDefinitions, visibleColumnKeys]);

  // Ensure default columns are visible on first load
  useEffect(() => {
    if (visibleColumnKeys.length === 0) {
      setVisibleColumnKeys(defaultVisibleColumns);
    }
  }, [visibleColumnKeys.length, setVisibleColumnKeys, defaultVisibleColumns]);

  const handleOpenDrawer = useCallback(() => {
    setDrawerVisible(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerVisible(false);
  }, []);

  const handleRowSelectionChange = useCallback((selectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(selectedRowKeys);
  }, []);

  const handleOpenCreateJobModal = useCallback(() => {
    setCreateJobModalVisible(true);
    // Set triggerStatusId from selectedTab when opening modal (only if not "all")
    if (selectedTab && selectedTab !== ALL_APPLICATIONS_TAB_KEY) {
      createJobForm.setFieldsValue({ triggerStatusId: selectedTab });
    }
  }, [createJobForm, selectedTab]);

  const handleCloseCreateJobModal = useCallback(() => {
    setCreateJobModalVisible(false);
    createJobForm.resetFields();
    setSelectedTemplateId(undefined);
  }, [createJobForm]);

  // Update triggerStatusId when selectedTab changes while modal is open
  useEffect(() => {
    if (createJobModalVisible && selectedTab && selectedTab !== ALL_APPLICATIONS_TAB_KEY) {
      createJobForm.setFieldsValue({ triggerStatusId: selectedTab });
    }
  }, [selectedTab, createJobModalVisible, createJobForm]);

  // Handle template selection - for visa application create job, we don't auto-populate fields
  // The template is only used when creating the job to get the configuration

  const handleTemplateChange = useCallback((value: string | number) => {
    setSelectedTemplateId(value ? String(value) : undefined);
  }, []);

  // Populate job name from template when template is selected
  useEffect(() => {
    if (selectedTemplateData?.data?.jobName && createJobModalVisible) {
      createJobForm.setFieldsValue({ jobName: selectedTemplateData.data.jobName });
    }
  }, [selectedTemplateData, createJobModalVisible, createJobForm]);

  // Get triggerStatusId - prioritize selectedTab over template
  // When a tab is selected, that's the filter the user applied, so we should validate against that
  const triggerStatusId = useMemo(() => {
    // First priority: selectedTab (if user has filtered by a specific status tab)
    if (selectedTab && selectedTab !== ALL_APPLICATIONS_TAB_KEY) {
      return selectedTab;
    }
    // Second priority: template's triggerStatusId (if template is selected but no tab filter)
    if (selectedTemplateData?.data?.triggerStatus?.id) {
      return selectedTemplateData.data.triggerStatus.id;
    }
    if (selectedTemplateData?.data?.triggerStatusId) {
      // Handle case where triggerStatusId might be a string or object
      const templateTriggerStatusId = selectedTemplateData.data.triggerStatusId;
      if (typeof templateTriggerStatusId === 'string') {
        return templateTriggerStatusId;
      }
      // If it's an object, try to get the id
      if (typeof templateTriggerStatusId === 'object' && templateTriggerStatusId !== null) {
        return (templateTriggerStatusId as any).id || null;
      }
    }
    return null;
  }, [selectedTemplateData, selectedTab]);

  // Query params for fetching application count based on trigger status
  const countQueryParams = useMemo(() => {
    if (
      !createJobModalVisible ||
      !selectedTemplateId ||
      selectedRowKeys.length > 0 ||
      !triggerStatusId
    ) {
      return null;
    }
    const filterString = `filters[visaStatus][op]=in&filters[visaStatus][val][0]=${triggerStatusId}`;
    return {
      ...queryParams,
      page: 1,
      limit: 1,
      filters: filterString,
    };
  }, [
    createJobModalVisible,
    selectedTemplateId,
    selectedRowKeys.length,
    triggerStatusId,
    queryParams,
  ]);

  // Fetch count of applications matching trigger status (only when needed)
  const { data: countData } = useVisaApplicationList(countQueryParams || queryParams, {
    enabled: countQueryParams !== null,
  });
  const applicationCount = countQueryParams !== null ? (countData?.totalDocs ?? 0) : 0;

  // Check if selected dataIds match trigger status
  const getMismatchedDataIdsCount = useMemo(() => {
    if (!triggerStatusId || selectedRowKeys.length === 0) {
      return 0;
    }

    const mismatchedCount = selectedRowKeys.filter((key) => {
      const visaApp = tableData.find((app) => app.id === key);
      if (!visaApp) return true;

      // Extract visa status ID - handle both object and string formats
      let visaStatusId: string | null = null;
      if (typeof visaApp.visaStatus === 'object' && visaApp.visaStatus !== null) {
        visaStatusId = (visaApp.visaStatus as Status).id;
      } else if (typeof visaApp.visaStatus === 'string') {
        visaStatusId = visaApp.visaStatus;
      }

      // Compare as strings to ensure exact match (handles type mismatches)
      return String(visaStatusId) !== String(triggerStatusId);
    }).length;

    return mismatchedCount;
  }, [selectedRowKeys, tableData, triggerStatusId]);

  // Check for duplicate template when relevant fields change
  useEffect(() => {
    if (!createJobModalVisible) {
      setDuplicateTemplateWarning(false);
      return;
    }

    // For visa application create job, we don't check for duplicate templates
    // since we only use template data to populate the job
    setDuplicateTemplateWarning(false);
  }, [createJobModalVisible]);

  const handleCreateJob = useCallback(async () => {
    try {
      const values = await createJobForm.validateFields();

      // Get template data to populate job fields
      if (!selectedTemplateId || !selectedTemplateData?.data) {
        message.error(
          t('jobs.create.template.required', { defaultValue: 'Please select a job template' })
        );
        return;
      }

      const template = selectedTemplateData.data;

      // Helper function to extract ID from status (handles both string and object formats)
      const extractStatusId = (statusId: any, statusObj: any): string | undefined => {
        // First try to get from status object
        if (statusObj?.id) {
          return String(statusObj.id);
        }
        // Then try statusId field
        if (statusId) {
          if (typeof statusId === 'string') {
            return statusId;
          }
          if (typeof statusId === 'object' && statusId !== null && statusId.id) {
            return String(statusId.id);
          }
        }
        return undefined;
      };

      // Get triggerStatusId - prioritize selectedTab, then template
      let triggerStatusIdValue: string = '';
      if (selectedTab && selectedTab !== ALL_APPLICATIONS_TAB_KEY) {
        triggerStatusIdValue = String(selectedTab);
      } else {
        const extracted = extractStatusId(template.triggerStatusId, template.triggerStatus);
        triggerStatusIdValue = extracted || '';
      }

      // Determine if we're using selected applications or all applications matching trigger status
      const useSelectedApplications = selectedRowKeys.length > 0;
      const totalApplications = useSelectedApplications ? selectedRowKeys.length : applicationCount;

      const payload = {
        jobName: (values.jobName || '').trim(),
        workflowId: template.workflowId || '',
        dataIds: useSelectedApplications ? (selectedRowKeys as string[]) : [],
        triggerStatusId: triggerStatusIdValue,
        successStatusId: extractStatusId(template.successStatusId, template.successStatus),
        failedBotStatusId: extractStatusId(template.failedBotStatusId, template.failedBotStatus),
        failedDataStatusId: extractStatusId(template.failedDataStatusId, template.failedDataStatus),
        failed: 0,
        total: totalApplications,
        success: 0,
        status: JobStatusEnum.PENDING,
        isTemplate: false, // Jobs created from visa applications are not templates
      };

      await createJobMutation.mutateAsync(payload);
      message.success(t('jobs.create.success', { defaultValue: 'Job created successfully' }));
      setSelectedRowKeys([]);
      handleCloseCreateJobModal();
    } catch (error: any) {
      // Ant Design Form validation errors have errorFields property
      if (error?.errorFields) {
        // Form validation errors - don't show error message, form will show field errors
        return;
      }
      const errorMessage = extractCreateError(
        error,
        t('jobs.create.error.default', { defaultValue: 'Failed to create job' })
      );
      message.error(errorMessage);
    }
  }, [
    createJobForm,
    selectedRowKeys,
    selectedTab,
    createJobMutation,
    t,
    handleCloseCreateJobModal,
    selectedTemplateId,
    selectedTemplateData,
    applicationCount,
  ]);

  return (
    <>
      <HeaderRow
        title={t('crud.list.title', { entity: t('visaApplications.title') })}
        createRoute="create"
        showCreateButton={rbac.hasAccess(userPermissions, 'visaApplication.create')}
        customContent={
          <>
            <ColumnVisibilitySelector
              columns={columnOptions}
              selectedKeys={visibleColumnKeys.filter((key) => key !== 'actions')}
              onChange={(keys) => setVisibleColumnKeys([...keys, 'actions'])}
              disabled={isLoading || isFetching}
            />
            <Button type="primary" size="large" onClick={handleOpenCreateJobModal}>
              {t('jobs.create.button', { defaultValue: 'Create Job' })}
            </Button>
            {rbac.hasAccess(userPermissions, 'visaApplication.create') && (
              <Button
                type="primary"
                size="large"
                icon={<UploadOutlined />}
                onClick={handleOpenDrawer}
              >
                {t('crud.create.upload')}
              </Button>
            )}
            {tableData.length > 0 && (
              <ExportModal
                onExport={handleExportVisaApplications}
                isLoading={isExportLoading}
                buttonText={t('common.export')}
                modalTitle={t('visaApplications.export.modal.title')}
                modalDescription={t('visaApplications.export.modal.description')}
                emailLabel={t('visaApplications.export.modal.emailLabel')}
                emailPlaceholder={t('visaApplications.export.modal.emailPlaceholder')}
                optionalNote={t('visaApplications.export.modal.optionalNote')}
                buttonType="primary"
                buttonSize="large"
                showIcon={true}
              />
            )}
          </>
        }
      />
      <VisaApplicationBulkImportContainer visible={drawerVisible} onClose={handleCloseDrawer} />
      <Modal
        title={t('jobs.create.modal.title', { defaultValue: 'Create Job' })}
        open={createJobModalVisible}
        onOk={handleCreateJob}
        onCancel={handleCloseCreateJobModal}
        confirmLoading={createJobMutation.isPending}
        okText={t('common.create', { defaultValue: 'Create' })}
        cancelText={t('common.cancel', { defaultValue: 'Cancel' })}
        okButtonProps={{
          disabled:
            getMismatchedDataIdsCount > 0 ||
            (selectedRowKeys.length === 0 &&
              applicationCount === 0 &&
              selectedTemplateId !== undefined),
        }}
      >
        <Form form={createJobForm} layout="vertical">
          <Form.Item
            name="templateId"
            label={t('jobs.create.template.label', { defaultValue: 'Job Template' })}
            tooltip={t('jobs.create.template.tooltip', {
              defaultValue: 'Select a template to automatically fill job fields',
            })}
            rules={[
              {
                required: true,
                message: t('jobs.create.template.required', {
                  defaultValue: 'Please select a job template',
                }),
              },
            ]}
          >
            <SelectField
              useListHook={useJobTemplateList}
              labelKey="jobName"
              valueKey="id"
              placeholder={t('jobs.create.template.placeholder', {
                defaultValue: 'Select a job template',
              })}
              value={selectedTemplateId}
              onChange={handleTemplateChange}
              required
            />
          </Form.Item>
          <Form.Item
            name="jobName"
            label={t('jobs.create.jobName.label', { defaultValue: 'Job Name' })}
            rules={[
              {
                required: true,
                message: t('jobs.create.jobName.required', {
                  defaultValue: 'Please enter a job name',
                }),
              },
              {
                validator: (_: any, value: string) => {
                  if (!value || value.trim().length === 0) {
                    return Promise.reject(
                      new Error(
                        t('jobs.create.jobName.required', {
                          defaultValue: 'Please enter a job name',
                        })
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder={t('jobs.create.jobName.placeholder', {
                defaultValue: 'Enter job name',
              })}
            />
          </Form.Item>

          {/* Info Alert - Display number of selected or total visa applications (Green) */}
          {selectedRowKeys.length > 0 ? (
            <Alert
              message={t('jobs.create.info.selectedApplications', {
                defaultValue: '{{count}} visa application(s) selected for this job',
                count: selectedRowKeys.length,
              })}
              type="success"
              showIcon
              style={{ marginTop: 8, marginBottom: 8 }}
            />
          ) : selectedTemplateId && triggerStatusId && applicationCount > 0 ? (
            <Alert
              message={t('jobs.create.info.allApplications', {
                defaultValue:
                  'All {{count}} visa application(s) matching the trigger status will be included',
                count: applicationCount,
              })}
              type="info"
              showIcon
              style={{ marginTop: 8, marginBottom: 8 }}
            />
          ) : null}

          {/* Warning Alert - Duplicate template configuration (Yellow) */}
          {duplicateTemplateWarning && (
            <Alert
              message={t('jobs.create.warning.duplicateTemplate', {
                defaultValue: 'Duplicate!! You are creating a new job with same configuration',
              })}
              type="warning"
              showIcon
              style={{ marginTop: 8, marginBottom: 8 }}
            />
          )}

          {/* Error Alert - Mismatched dataIds (Red) */}
          {getMismatchedDataIdsCount > 0 && (
            <Alert
              message={t('jobs.create.error.mismatchedStatus', {
                defaultValue:
                  '{{count}} visa application(s) do not match the selected trigger status',
                count: getMismatchedDataIdsCount,
              })}
              type="error"
              showIcon
              style={{ marginTop: 8, marginBottom: 8 }}
            />
          )}
        </Form>
      </Modal>
      <div className="row mt-3">
        <VisaApplicationTable
          data={tableData as any}
          columns={columns}
          loading={isLoading || deleteMutation.isPending || isFetching || isAvailableFiltersLoading}
          pagination={data}
          rowKey="id"
          showSizeChanger={true}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSearchBar={true}
          onSearchChange={handleSearchChange}
          showFilters={true}
          availableFilters={transformedFilters}
          onFilterChange={handleVisaFilterChange}
          tableProps={{
            onChange: handleTableChange,
            sortDirections: ['ascend', 'descend'],
            rowSelection: {
              selectedRowKeys,
              onChange: handleRowSelectionChange,
            },
          }}
          sortField={queryParams.sortField}
          sortOrder={queryParams.sortOrder}
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
          statuses={statuses}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(VisaApplicationTableContainer, 'visaApplication.view');
